// src/components/Sidebar.jsx
import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  FaTachometerAlt, FaNotesMedical,
  FaUserAlt, FaSignOutAlt, FaBars, FaTimes, FaClipboardCheck,
  
  FaUser,
} from 'react-icons/fa';
 
const Sidebar = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => logout();
  const toggleSidebar = () => setIsOpen(!isOpen);

  const navigation = [
    { name: 'Dashboard',        path: '/dashboard',              icon: <FaTachometerAlt />,  roles: ['admin'] },
    { name: 'Products',         path: '/dashboard/products',     icon: <FaClipboardCheck />, roles: ['admin'] },
    { name: 'Clients',          path: '/dashboard/clients',      icon: <FaUser />,           roles: ['admin'] },
    { name: 'Proforma Invoice', path: '/dashboard/pi',           icon: <FaNotesMedical />,   roles: ['admin'] },
    { name: 'PI List', path: '/dashboard/pi-list',           icon: <FaNotesMedical />,   roles: ['admin'] },
    { name: 'All Patients',     path: '/dashboard/user-list',    icon: <FaClipboardCheck />, roles: ['receptionist', 'doctor'] },
    { name: 'Create OPD Form',  path: '/dashboard/opd-form',     icon: <FaNotesMedical />,   roles: ['receptionist', 'doctor'] },
    { name: 'OPD List',         path: '/dashboard/opd-list',     icon: <FaClipboardCheck />, roles: ['receptionist', 'doctor'] },
    { name: 'Profile',          path: '/dashboard/profile',      icon: <FaUserAlt />,        roles: ['patient'] },
  ];

  const filteredNavigation = navigation.filter(item => item.roles.includes(user?.role));
  const isActive = (path) => location.pathname === path;

  const roleBadge = {
    patient:      { bg: 'rgba(99,102,241,0.1)',   border: 'rgba(99,102,241,0.25)',  text: '#6c63ff' },
    doctor:       { bg: 'rgba(20,184,166,0.1)',   border: 'rgba(20,184,166,0.25)',  text: '#0d9488' },
    receptionist: { bg: 'rgba(245,158,11,0.1)',   border: 'rgba(245,158,11,0.25)',  text: '#d97706' },
  };
  const badge = roleBadge[user?.role] || roleBadge.patient;
  const initials = user?.email?.slice(0, 2).toUpperCase() || 'U';

  return (
    <>
      {/* ── Mobile Header ── */}
      <div className="md:hidden flex items-center justify-between bg-[#f8f8ff] border-b border-[#d0d0e8] px-[18px] py-[14px] sticky top-0 z-30 shadow-sm">
        <span
          className="text-[#1a1a2e] text-lg font-medium tracking-tight"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          MediCare
        </span>
        <button
          onClick={toggleSidebar}
          aria-label="Toggle Sidebar"
          className="w-9 h-9 rounded-[9px] flex items-center justify-center bg-[rgba(124,109,250,0.06)] border border-[#d0d0e8] text-[#4a4a75] cursor-pointer transition-all duration-200 hover:bg-[rgba(108,99,255,0.1)] hover:border-[rgba(108,99,255,0.3)] hover:text-[#6c63ff]"
        >
          {isOpen ? <FaTimes size={13} /> : <FaBars size={13} />}
        </button>
      </div>

      {/* ── Overlay ── */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar Panel ── */}
      <div
        className={`
          fixed top-0 left-0 h-screen w-[260px] bg-[#f8f8ff]
          border-r border-[#d0d0e8] flex flex-col z-50
          shadow-[4px_0_40px_rgba(0,0,0,0.08)]
          transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          md:static md:translate-x-0 md:shadow-none md:h-screen
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Ambient glow */}
        <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-[radial-gradient(circle,rgba(108,99,255,0.07),transparent_70%)] pointer-events-none" />

        {/* ── Logo ── */}
        <div className="px-5 pt-[22px] pb-5 border-b border-[#d0d0e8] relative">
          <Link to="#" className="flex items-center gap-2 no-underline" onClick={() => setIsOpen(false)}>
            <div className="bg-white rounded-xl px-3 py-2 shadow-md border border-[#d0d0e8] inline-flex items-center justify-center w-full">
              <img src="/logo.png" alt="logo" className="h-8 w-auto object-contain" />
            </div>
          </Link>
        </div>

        {/* ── User Card ── */}
        <div className="mx-[14px] my-4 px-[14px] py-3 bg-[rgba(124,109,250,0.05)] border border-[#d0d0e8] rounded-2xl flex items-center gap-[10px]">
          <div className="w-9 h-9 rounded-[10px] flex-shrink-0 flex items-center justify-center text-xs font-semibold text-white tracking-wider bg-gradient-to-br from-[#6c63ff] to-[#a855f7]">
            {initials}
          </div>
          <div className="overflow-hidden flex-1">
            <p className="text-[0.76rem] text-[#4a4a75] font-normal truncate">{user?.email}</p>
            <span
              className="inline-block mt-[3px] px-2 py-[2px] rounded-full text-[0.62rem] font-medium tracking-widest uppercase"
              style={{ background: badge.bg, border: `1px solid ${badge.border}`, color: badge.text }}
            >
              {user?.role}
            </span>
          </div>
        </div>

        {/* ── Navigation ── */}
        <nav className="flex-1 px-[10px] overflow-y-auto">
          <p className="px-[18px] mt-4 mb-[6px] text-[0.62rem] font-medium tracking-[0.14em] uppercase text-[#6e6e94]">
            Navigation
          </p>
          <ul className="list-none m-0 p-0 space-y-[2px]">
            {filteredNavigation.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  className={`
                    relative flex items-center gap-[11px] px-3 py-[10px] rounded-[11px]
                    no-underline text-[0.86rem] font-normal transition-all duration-200 overflow-hidden
                    ${isActive(item.path)
                      ? 'bg-[rgba(108,99,255,0.1)] text-[#6c63ff] border border-[rgba(108,99,255,0.2)]'
                      : 'text-[#4a4a75] border border-transparent hover:bg-[rgba(108,99,255,0.06)] hover:text-[#1a1a2e]'
                    }
                  `}
                >
                  {/* Active left bar */}
                  {isActive(item.path) && (
                    <span className="absolute left-0 top-[20%] bottom-[20%] w-[3px] rounded-r-sm bg-gradient-to-b from-[#6c63ff] to-[#a855f7]" />
                  )}

                  {/* Icon tile */}
                  <span
                    className={`
                      w-8 h-8 rounded-lg flex items-center justify-center text-[0.85rem] flex-shrink-0 transition-all duration-200
                      ${isActive(item.path)
                        ? 'bg-[rgba(108,99,255,0.15)] text-[#6c63ff]'
                        : 'bg-[rgba(124,109,250,0.07)] text-[#6c6c96]'
                      }
                    `}
                  >
                    {item.icon}
                  </span>
                  <span>{item.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* ── Footer / Logout ── */}
        <div className="px-[10px] pb-5 pt-3 border-t border-[#d0d0e8]">
          <button
            onClick={() => { handleLogout(); setIsOpen(false); }}
            aria-label="Logout"
            className="group w-full flex items-center gap-[11px] px-3 py-[10px] rounded-[11px] border-none cursor-pointer bg-transparent text-[0.86rem] font-normal text-[#4a4a75] transition-all duration-200 hover:bg-red-500/[0.07] hover:text-red-500"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-[0.85rem] flex-shrink-0 bg-[rgba(124,109,250,0.07)] text-[#6c6c96] transition-all duration-200 group-hover:bg-red-500/[0.1] group-hover:text-red-500">
              <FaSignOutAlt />
            </span>
            <span>Sign out</span>
          </button>
        </div>

      </div>
    </>
  );
};

export default Sidebar;